import { ComponentDataSourceService } from '../../component-data-source.service';
import { Observable, map, of, take } from 'rxjs';
import { DataSourceFunction } from '../../component-data-source.service';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { environment } from '../../../../environment/environment';
import { EventParticipantDTO } from '../../../../dto/event/event-participant.dto';
import { EventDTO } from '../../../../dto/event/event.dto';
import { Router } from '@angular/router';
import { ComponentDataDestService } from '../../../component-data-dest/component-data-dest.service';
import * as DataActions from '../../../../ngrx/data/data.actions';
import {
  eventParticipantDisplayTotalCount,
  filterParticipantDisplayItemsBySearch,
} from './event-participant-search.util';

const fetchEventData = (service: ComponentDataSourceService, eventGuid: string): Observable<EventDTO> =>
  service.http.get<EventDTO>(`${environment.apiUrl}/events/${eventGuid}`);

/** Extra deltagere på venteliste har ofte næsten tom `data` i API; vis samme info som hovedtilmelderen. */
function mergeParticipantDataForSubtitle(
  p: EventParticipantDTO,
  registrant: EventParticipantDTO | undefined
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...((p.data as Record<string, unknown>) || {}) };
  if (!registrant?.data) return out;
  const rd = registrant.data as Record<string, unknown>;

  const isEmptyStr = (v: unknown) => v == null || (typeof v === 'string' && !String(v).trim());
  const isEmptyArr = (v: unknown) => !Array.isArray(v) || v.length === 0;

  if (isEmptyStr(out['comments']) && !isEmptyStr(rd['comments'])) out['comments'] = rd['comments'];
  if (isEmptyStr(out['additional_comments']) && !isEmptyStr(rd['additional_comments'])) {
    out['additional_comments'] = rd['additional_comments'];
  }
  if (isEmptyArr(out['catering_preferences'])) {
    if (Array.isArray(rd['catering_preferences']) && rd['catering_preferences'].length) {
      out['catering_preferences'] = rd['catering_preferences'];
    } else if (Array.isArray(rd['dietary_preferences']) && rd['dietary_preferences'].length) {
      out['catering_preferences'] = rd['dietary_preferences'];
    }
  }
  if (!out['added_by'] && registrant.user) {
    const nm = `${registrant.user.first_name || ''} ${registrant.user.last_name || ''}`.trim();
    if (nm) out['added_by'] = { name: nm };
  }
  return out;
}

// Interface for display purposes only - flattened structure for the table
interface ParticipantDisplayData extends EventParticipantDTO {
  name: string;
  email: string;
  organization: string;
}

export const event_waitlist = (service: ComponentDataSourceService): DataSourceFunction => {
  // Event organization info for cross-org detection
  let eventOrganizationId: number | null = null;

  // Whether to show the organisation column (set after data loads)
  let showOrganizationColumn = false;

  const buildWaitlistColumns = (): any[] => {
    const cols: any[] = [
      {
        key: 'name',
        header: 'Navn',
        type: 'text',
        width: '250px',
        transform: (value: string) => value || '-',
        subtitle: (rowData: ParticipantDisplayData) => {
          const lines: string[] = [];

          if (rowData.user?.locations && rowData.user.locations.length > 0) {
            const locName = rowData.user.locations[0].locations_name;
            if (locName) lines.push(locName);
          }

          const commentParts: string[] = [];
          if (rowData.data?.comments && rowData.data.comments.trim()) {
            commentParts.push(rowData.data.comments.trim());
          }
          if (
            rowData.data?.additional_comments &&
            rowData.data.additional_comments.trim() &&
            rowData.data.additional_comments !== rowData.data.comments
          ) {
            commentParts.push(rowData.data.additional_comments.trim());
          }
          if (commentParts.length > 0) {
            lines.push(commentParts.join(' • '));
          }

          const cateringPrefs = rowData.data?.catering_preferences;
          const dietaryPrefs = rowData.data?.dietary_preferences;
          const prefLine =
            cateringPrefs && Array.isArray(cateringPrefs) && cateringPrefs.length > 0
              ? cateringPrefs.join(', ')
              : dietaryPrefs && Array.isArray(dietaryPrefs) && dietaryPrefs.length > 0
                ? dietaryPrefs.join(', ')
                : null;
          if (prefLine) {
            lines.push(`Catering: ${prefLine}`);
          }

          if (rowData.data?.added_by) {
            lines.push(`Tilmeldt af: ${rowData.data.added_by.name}`);
          }

          return lines.length > 0 ? lines.join('\n') : null;
        }
      }
    ];

    // Show the organisation column only when at least one participant is from a
    // different org than the event. When shown, all rows display their org.
    if (showOrganizationColumn) {
      cols.push({
        key: 'organization',
        header: 'Organisation',
        type: 'text',
        width: '160px',
        transform: (value: string) => value || '-'
      });
    }

    cols.push(
      {
        key: 'email',
        header: 'Email',
        type: 'text',
        transform: (value: string) => value || '-'
      },
      {
        key: 'role',
        header: 'Rolle',
        type: 'select',
        editable: true,
        editor: 'select',
        options: [
          { value: 'attendee', label: 'Deltager' },
          { value: 'instructor', label: 'Instruktør' }
        ],
        onChange: (newValue: string, rowData: ParticipantDisplayData) => {
          if (rowData.participant_id && rowData.participant_id > 0) {
            const router = service.injector.get(Router);
            const pathSegments = router.url.split('/');
            const eventGuid = pathSegments.find((segment: string) =>
              /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
            );

            service.injector.get(ComponentDataDestService).getDataDest('update_event_participation')?.({
              eventGuid,
              user_id: rowData.participant_id,
              user_uuid: rowData.user?.uuid,
              role: newValue,
              status: rowData.status
            });
          }
        },
        transform: (value: string) => {
          const roles: Record<string, string> = {
            attendee: 'Deltager',
            instructor: 'Instruktør',
            assistant: 'Assistent',
            empty: '-'
          };
          return roles[value || 'empty'] || value || '-';
        }
      },
      {
        key: 'status',
        header: 'Status',
        type: 'select',
        editable: true,
        editor: 'select',
        options: [
          { value: 'waitlist', label: 'Venteliste' },
          { value: 'pending', label: 'Tilmeldt' },
          { value: 'shownup', label: 'Mødt op' },
          { value: 'requires_attention', label: 'Kræver opmærksomhed' },
          { value: 'completed', label: 'Gennemført' },
          { value: 'not_appear', label: 'Udeblevet' },
          { value: 'cancellation', label: 'Afbud' },
          { value: 'not_participant', label: 'Ikke deltager' }
        ],
        onChange: (newValue: string, rowData: ParticipantDisplayData) => {
          if (rowData.participant_id && rowData.participant_id > 0) {
            const router = service.injector.get(Router);
            const pathSegments = router.url.split('/');
            const eventGuid = pathSegments.find((segment: string) =>
              /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
            );

            service.injector.get(ComponentDataDestService).getDataDest('update_event_participation')?.({
              eventGuid,
              user_id: rowData.participant_id,
              user_uuid: rowData.user?.uuid,
              role: rowData.role,
              status: newValue
            });
          }
        },
        transform: (value: string) => {
          const statuses: Record<string, string> = {
            waitlist: 'Venteliste',
            pending: 'Tilmeldt',
            shownup: 'Mødt op',
            requires_attention: 'Kræver opmærksomhed',
            completed: 'Gennemført',
            not_appear: 'Udeblevet',
            cancellation: 'Afbud',
            not_participant: 'Ikke deltager',
            empty: '-'
          };
          return statuses[value || 'empty'] || value || '-';
        }
      }
    );

    return cols;
  };

  return {
    name: 'event_waitlist',
    description: 'Get event waitlist participants',
    execute: (params?: LoadDataParams): Observable<{ items: ParticipantDisplayData[]; totalCount: number }> => {
      const router = service.injector.get(Router);
      const pathSegments = router.url.split('/');
      const eventGuid = pathSegments.find(segment =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
      );

      if (!eventGuid) {
        throw new Error('No valid event GUID found in URL');
      }

      return fetchEventData(service, eventGuid).pipe(
        map((event: EventDTO) => {

          const participants = event.participants?.filter((p: EventParticipantDTO) => p.status === 'waitlist') || [];

          const registrantForAddedUuid = (uuid: string | undefined): EventParticipantDTO | undefined => {
            if (!uuid || !event.participants) return undefined;
            return event.participants.find(
              ep =>
                Array.isArray((ep.data as any)?.added_participants) &&
                (ep.data as any).added_participants.some((ap: any) => ap.uuid === uuid)
            );
          };

          let items: ParticipantDisplayData[] =
            participants.length > 0
              ? participants.map(p => {
                  const registrant = registrantForAddedUuid(p.user?.uuid);

                  let organizationDisplay = '';
                  if (p.user?.locations && p.user.locations.length > 0) {
                    organizationDisplay = p.user.locations[0].data?.org_name || '';
                  } else {
                    organizationDisplay = p.data?.organization?.trim() || '';
                  }

                  if (!organizationDisplay && registrant) {
                    if (registrant.user?.locations && registrant.user.locations.length > 0) {
                      organizationDisplay = registrant.user.locations[0].data?.org_name || '';
                    }
                    if (!organizationDisplay) {
                      organizationDisplay = registrant.data?.organization?.trim() || '';
                    }
                  }

                  const mergedData = mergeParticipantDataForSubtitle(p, registrant);

                  return {
                    ...p,
                    data: mergedData as any,
                    name: `${p.user?.first_name || ''} ${p.user?.last_name || ''}`.trim() || '-',
                    email: p.user?.user || '-',
                    organization: organizationDisplay
                  };
                })
              : [
                  {
                    participant_id: 0,
                    event_id: 0,
                    user: {
                      uuid: '',
                      first_name: 'Der er endnu ingen data',
                      last_name: '',
                      user: '',
                      roles: null,
                      organizations_id: null,
                      locations: null,
                      status: null
                    },
                    role: 'empty',
                    status: 'empty',
                    joined_at: undefined,
                    data: null,
                    name: 'Der er endnu ingen data',
                    email: '',
                    organization: ''
                  }
                ];

          // Determine whether to show the organisation column: only when at least one
          // waitlist participant is from a different org than the event.
          eventOrganizationId = (event as any).organizations_id ?? null;
          showOrganizationColumn = items.some(item => {
            if (item.user?.locations && item.user.locations.length > 0) {
              const locOrgId: number | null = (item.user.locations[0] as any)?.data?.organizations_id ?? null;
              return locOrgId !== null && eventOrganizationId !== null && locOrgId !== eventOrganizationId;
            }
            // Participants without registered locations: any org text (direct or inherited) counts as cross-org
            return !!(item.organization?.trim());
          });

          items = filterParticipantDisplayItemsBySearch(
            items,
            params?.searchQuery ?? params?.queryParams?.['searchQuery']
          );

          if (params?.filters) {
            const sortColumn = params.filters.find(f => f.column === 'sortColumn')?.value as string;
            const sortDirection = params.filters.find(f => f.column === 'sortDirection')?.value as 'asc' | 'desc';

            if (sortColumn && sortDirection && items.length > 0 && items[0].participant_id !== 0) {
              items.sort((a, b) => {
                let aValue: any;
                let bValue: any;
                switch (sortColumn) {
                  case 'name':
                  case 'first_name':
                  case 'last_name':
                    aValue = a.name;
                    bValue = b.name;
                    break;
                  case 'email':
                    aValue = a.email;
                    bValue = b.email;
                    break;
                  case 'organization':
                    aValue = a.organization;
                    bValue = b.organization;
                    break;
                  case 'role':
                    aValue = a.role;
                    bValue = b.role;
                    break;
                  case 'status':
                    aValue = a.status;
                    bValue = b.status;
                    break;
                  default:
                    aValue = (a as any)[sortColumn];
                    bValue = (b as any)[sortColumn];
                }

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                  return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
                }
                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
              });
            }
          }

          return {
            items,
            totalCount: eventParticipantDisplayTotalCount(items),
          };
        })
      );
    },
    settings: {
      get columns() {
        return buildWaitlistColumns();
      },
      pageSize: 10,
      pageSizeOptions: [10, 25, 50, 100],
      pageable: true,
      sortable: true,
      rowHover: true,
      searchable: true,
      defaultSortColumn: 'name',
      defaultSortDirection: 'asc',
      multiSelect: true,
      showRemoveButton: true,
      removeButtonText: 'Slet valgte',
      removeButtonColor: 'warn',
      onRemoveSelected: (selectedItems: any[]) => {
        const participantUuids = selectedItems
          .filter(item => item.user?.uuid && item.participant_id > 0)
          .map(item => item.user.uuid);

        if (participantUuids.length === 0) {
          return;
        }

        const router = service.injector.get(Router);
        const pathSegments = (router.url || '').split('?')[0].split('/');
        const eventGuid = pathSegments.find((segment: string) =>
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)
        );

        if (!eventGuid) {
          return;
        }

        service.http
          .delete(`${environment.apiUrl}/events/${eventGuid}/participants`, {
            body: { participant_uuids: participantUuids }
          })
          .subscribe({
            next: () => {
              service.store
                .select((state: any) => state.data?.['event_waitlist'])
                .pipe(take(1))
                .subscribe((currentData: any) => {
                  if (currentData?.data?.items) {
                    const updatedItems = currentData.data.items.filter(
                      (item: any) => !participantUuids.includes(item.user?.uuid)
                    );
                    service.store.dispatch(
                      DataActions.loadDataSuccess({
                        dataSourceKey: 'event_waitlist',
                        data: {
                          ...currentData.data,
                          items: updatedItems,
                          totalCount: updatedItems.length
                        },
                        settings: currentData.settings || {}
                      })
                    );
                  }
                });
            },
            error: (error: any) => {
              console.error('[Waitlist] Error removing participants:', error);
            }
          });
      }
    }
  };
};
